<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { formatBytes } from 'shared/helpers/FileHelper';
import { downloadFile } from '@chatwoot/utils';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'shared/components/Spinner.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'update:show']);

const show = defineModel('show', { type: Boolean, default: false });

const store = useStore();
const { t } = useI18n();

const attachments = computed(() => {
  return store.getters.getSelectedChatAttachments || [];
});

const isLoading = ref(false);
const isDownloading = ref({});
const activeTab = ref('all');
const showGallery = ref(false);
const activeAttachment = ref(null);

const fileTypeGroups = computed(() => {
  const groups = {
    media: [],
    audio: [],
    document: [],
    all: [],
  };

  attachments.value.forEach(attachment => {
    const fileType = attachment.file_type || attachment.fileType;
    const data = {
      ...attachment,
      fileName: getFileName(attachment),
      fileExtension: getFileExtension(attachment),
      fileSize: formatBytes(attachment.file_size || 0, 0),
      createdAt: attachment.created_at || attachment.createdAt,
      sender: attachment.sender || {},
    };

    groups.all.push(data);

    if (['image', 'ig_reel', 'video'].includes(fileType)) {
      groups.media.push(data);
    } else if (['audio'].includes(fileType)) {
      groups.audio.push(data);
    } else {
      groups.document.push(data);
    }
  });

  // Sort by date desc
  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  });

  return groups;
});

const tabs = [
  { key: 'all', label: 'All', icon: 'i-lucide-grid-3x3' },
  { key: 'media', label: 'Photos & Videos', icon: 'i-lucide-image' },
  { key: 'document', label: 'Documents', icon: 'i-lucide-file-text' },
  { key: 'audio', label: 'Audio', icon: 'i-lucide-headphones' },
];

const currentTabAttachments = computed(() => {
  return fileTypeGroups.value[activeTab.value] || [];
});

const currentTabCount = computed(() => {
  return currentTabAttachments.value.length;
});

const hasAttachments = computed(() => {
  return attachments.value.length > 0;
});

const totalAttachments = computed(() => {
  return attachments.value.length;
});

const getFileName = attachment => {
  const dataUrl = attachment.data_url || attachment.dataUrl || '';
  if (dataUrl) {
    const fileName = dataUrl.substring(dataUrl.lastIndexOf('/') + 1);
    return decodeURIComponent(fileName) || 'Unknown File';
  }
  return attachment.fallback_title || 'Unknown File';
};

const getFileExtension = attachment => {
  const fileName = getFileName(attachment);
  return fileName.split('.').pop()?.toLowerCase() || '';
};

const handleDownload = async attachment => {
  const attachmentId = attachment.id;
  isDownloading.value[attachmentId] = true;

  try {
    const dataUrl = attachment.data_url || attachment.dataUrl;
    const fileType = attachment.file_type || attachment.fileType;
    const extension = getFileExtension(attachment);

    if (dataUrl) {
      await downloadFile({ url: dataUrl, type: fileType, extension });
    } else {
      useAlert('Unable to download file. Please try again');
    }
  } catch (error) {
    useAlert('Unable to download file. Please try again');
  } finally {
    isDownloading.value[attachmentId] = false;
  }
};

const openInNewTab = attachment => {
  const dataUrl = attachment.data_url || attachment.dataUrl;
  if (dataUrl) {
    window.open(dataUrl, '_blank', 'noopener,noreferrer');
  }
};

const openGallery = attachment => {
  const fileType = attachment.file_type || attachment.fileType;
  if (['image', 'video', 'ig_reel'].includes(fileType)) {
    activeAttachment.value = attachment;
    showGallery.value = true;
  } else {
    openInNewTab(attachment);
  }
};

const formatDate = timestamp => {
  if (!timestamp) return '';
  return messageTimestamp(timestamp, 'MMM d, yyyy') || '';
};

const getSenderName = sender => {
  return sender?.name || sender?.available_name || 'Unknown';
};

const getSenderAvatar = sender => {
  return sender?.thumbnail || sender?.avatar_url || '';
};

const fetchAttachments = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('fetchAllAttachments', props.conversationId);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (show.value && props.conversationId) {
    fetchAttachments();
  }
});

watch(show, newValue => {
  if (newValue && props.conversationId) {
    fetchAttachments();
  }
});

const handleClose = () => {
  show.value = false;
  emit('close');
  emit('update:show', false);
};
</script>

<template>
  <Modal
    v-model:show="show"
    size="medium"
    :show-close-button="false"
    @close="handleClose"
  >
    <div class="flex flex-col h-[85vh] max-h-[900px] w-full bg-n-background">
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0 bg-n-solid-1"
      >
        <div class="flex items-center gap-3">
          <Button
            ghost
            slate
            icon="i-lucide-arrow-left"
            size="sm"
            class="rounded-lg"
            @click="handleClose"
          />
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12 m-0">
              Media, Links and Docs
            </h3>
            <p v-if="hasAttachments" class="text-sm text-n-slate-11 m-0">
              {{ totalAttachments }}
              {{ totalAttachments === 1 ? 'item' : 'items' }}
            </p>
          </div>
        </div>
        <Button
          ghost
          slate
          icon="i-lucide-x"
          size="sm"
          class="rounded-lg"
          @click.stop="handleClose"
        />
      </div>

      <!-- Tabs -->
      <div
        v-if="hasAttachments"
        class="flex items-center gap-1 px-4 pt-3 pb-2 border-b border-n-weak shrink-0 bg-n-solid-1 overflow-x-auto"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
          :class="[
            activeTab === tab.key
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-slate-2',
          ]"
          @click="activeTab = tab.key"
        >
          <Icon :icon="tab.icon" class="size-4" />
          <span>{{ tab.label }}</span>
          <span
            v-if="fileTypeGroups[tab.key]?.length > 0"
            class="px-1.5 py-0.5 rounded text-xs"
            :class="[activeTab === tab.key ? 'bg-white/20' : 'bg-n-slate-3']"
          >
            {{ fileTypeGroups[tab.key].length }}
          </span>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <div v-if="isLoading" class="flex items-center justify-center h-full">
          <Spinner size="" />
        </div>

        <div
          v-else-if="!hasAttachments"
          class="flex flex-col items-center justify-center h-full p-8"
        >
          <div
            class="size-20 rounded-full bg-n-slate-3 flex items-center justify-center mb-4"
          >
            <Icon icon="i-lucide-paperclip" class="text-n-slate-9 size-10" />
          </div>
          <p class="text-n-slate-11 text-center font-medium text-base">
            No files shared yet
          </p>
          <p class="text-n-slate-10 text-center text-sm mt-1">
            Files shared in this conversation will appear here
          </p>
        </div>

        <div
          v-else-if="currentTabAttachments.length === 0"
          class="flex flex-col items-center justify-center h-full p-8"
        >
          <div
            class="size-16 rounded-full bg-n-slate-3 flex items-center justify-center mb-3"
          >
            <Icon
              :icon="
                tabs.find(t => t.key === activeTab)?.icon || 'i-lucide-file'
              "
              class="text-n-slate-9 size-8"
            />
          </div>
          <p class="text-n-slate-11 text-center font-medium">
            No
            {{ tabs.find(t => t.key === activeTab)?.label.toLowerCase() }} found
          </p>
        </div>

        <!-- Media Grid (WhatsApp style) -->
        <div v-else-if="activeTab === 'media'" class="p-4">
          <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <div
              v-for="attachment in currentTabAttachments"
              :key="attachment.id"
              class="group relative aspect-square rounded-lg overflow-hidden cursor-pointer bg-n-slate-3 border border-n-weak hover:border-n-brand/50 transition-all"
              @click="openGallery(attachment)"
            >
              <img
                v-if="
                  ['image', 'ig_reel'].includes(
                    attachment.file_type || attachment.fileType
                  )
                "
                :src="attachment.data_url || attachment.dataUrl"
                :alt="attachment.fileName"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                v-else
                class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-n-slate-2 to-n-slate-3"
              >
                <Icon
                  icon="i-lucide-play"
                  class="size-8 text-n-slate-11 mb-1"
                />
                <span class="text-[10px] text-n-slate-10 font-medium uppercase">
                  {{ attachment.fileExtension }}
                </span>
              </div>

              <!-- Video duration badge -->
              <div
                v-if="
                  ['video', 'ig_reel'].includes(
                    attachment.file_type || attachment.fileType
                  )
                "
                class="absolute bottom-1 right-1 bg-black/70 backdrop-blur-sm rounded px-1.5 py-0.5 flex items-center gap-1"
              >
                <Icon icon="i-lucide-play" class="size-3 text-white" />
              </div>

              <!-- Hover overlay -->
              <div
                class="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors"
              />
            </div>
          </div>
        </div>

        <!-- Documents & Audio List (WhatsApp style) -->
        <div v-else class="p-4 space-y-1">
          <div
            v-for="attachment in currentTabAttachments"
            :key="attachment.id"
            class="group flex items-center gap-3 p-3 rounded-xl hover:bg-n-slate-2 transition-colors cursor-pointer"
            @click="openInNewTab(attachment)"
          >
            <!-- File Icon -->
            <div
              class="size-12 rounded-xl bg-n-slate-3 flex items-center justify-center shrink-0 group-hover:bg-n-slate-4 transition-colors"
            >
              <FileIcon
                v-if="activeTab === 'document' || activeTab === 'all'"
                :file-type="attachment.fileExtension"
                class="size-7"
              />
              <Icon
                v-else
                icon="i-lucide-music"
                class="size-6 text-n-slate-11"
              />
            </div>

            <!-- File Info -->
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium text-n-slate-12 truncate m-0 mb-0.5"
              >
                {{ attachment.fileName }}
              </p>
              <div class="flex items-center gap-2 text-xs text-n-slate-10">
                <span>{{ attachment.fileSize }}</span>
                <span>•</span>
                <span>{{ formatDate(attachment.createdAt) }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div
              class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Button
                ghost
                slate
                size="sm"
                icon="i-lucide-download"
                :is-loading="isDownloading[attachment.id]"
                :disabled="isDownloading[attachment.id]"
                @click.stop="handleDownload(attachment)"
              />
              <Button
                ghost
                slate
                size="sm"
                icon="i-lucide-external-link"
                @click.stop="openInNewTab(attachment)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>

  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="activeAttachment"
    :all-attachments="fileTypeGroups.media"
    @close="showGallery = false"
  />
</template>
